import { useState } from "react";
import { toast } from "sonner";
import { X, Download, Check } from "lucide-react";
import { api } from "@/lib/api";

const ResultRow = ({ it, sel, tags, onToggle, onTags }) => (
  <div className={`flex gap-3 p-2 border-b border-black/15 ${sel ? "bg-[#FFF4E5]" : ""}`} data-testid={`seasonal-row-${it.slug}`}>
    <input type="checkbox" checked={sel} onChange={onToggle} className="w-4 h-4 mt-1 shrink-0" data-testid={`seasonal-check-${it.slug}`} />
    <img src={it.image_url} alt="" className="w-14 h-14 object-cover border-2 border-black shrink-0" />
    <div className="flex-1 min-w-0 space-y-1">
      <div className="text-sm font-bold leading-tight line-clamp-2">{it.title}</div>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest">
        <span>${Number(it.price).toFixed(2)}</span>
        <span className="opacity-60">· {it.query}</span>
        {it.exists && <span className="pcs-bg-blue text-white px-1" data-testid={`seasonal-exists-${it.slug}`}>Already in catalog</span>}
      </div>
      <input value={tags} onChange={(e) => onTags(e.target.value)} data-testid={`seasonal-tags-${it.slug}`} className="w-full border-2 border-black px-2 py-1 text-xs font-mono" />
    </div>
  </div>
);

export default function SeasonalImportDialog({ campaign, onClose }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [tags, setTags] = useState({});
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState(15);

  const search = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/admin/campaigns/${campaign.id}/import/preview`, { limit_per_query: limit });
      setData(d);
      setSel(Object.fromEntries(d.items.map((i) => [i.slug, !i.exists])));
      setTags(Object.fromEntries(d.items.map((i) => [i.slug, i.suggested_tags.join(", ")])));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally {
      setBusy(false);
    }
  };

  const chosen = data ? data.items.filter((i) => sel[i.slug]) : [];
  const commit = async () => {
    setBusy(true);
    try {
      const items = chosen.map(({ slug, title, description, image_url, price, currency, affiliate_url }) => ({
        slug, title, description, image_url, price, currency, affiliate_url,
        tags: tags[slug].split(",").map((t) => t.trim()).filter(Boolean),
      }));
      const { data: r } = await api.post(`/admin/campaigns/${campaign.id}/import/commit`, { items });
      toast.success(`Imported ${r.created} new, updated ${r.updated}`);
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="seasonal-import-dialog">
      <div className="brut-lg bg-white w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center gap-2 p-4 border-b-2 border-black">
          <Download className="w-5 h-5" strokeWidth={2.5} />
          <h3 className="font-display text-2xl tracking-tight">Seasonal import · {campaign.name}</h3>
          <button onClick={() => onClose(false)} className="ml-auto" data-testid="seasonal-import-close" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b-2 border-black space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest opacity-70">
            eBay searches: {campaign.import_queries.map((q) => q.query).join(" · ") || "none set"}
            {campaign.pick_categories.length > 0 && <> → category {campaign.pick_categories.join(", ")}</>}
          </div>
          <div className="flex items-center gap-2">
            <label className="font-mono text-[10px] uppercase tracking-widest">Per search</label>
            <input type="number" min="1" max="50" value={limit} onChange={(e) => setLimit(Number(e.target.value) || 15)} data-testid="seasonal-import-limit" className="w-16 border-2 border-black px-2 py-1 text-sm" />
            <button onClick={search} disabled={busy || !campaign.import_queries.length} data-testid="seasonal-import-search" className="brut pcs-bg-blue text-white px-3 py-1.5 font-bold uppercase text-xs brut-press disabled:opacity-50">
              {busy && !data ? "Searching..." : data ? "Search again" : "Search eBay"}
            </button>
            <span className="text-xs opacity-60">Amazon isn't searchable yet — add Amazon items via Paste URL → Quick fill.</span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="seasonal-import-results">
          {data && data.items.length === 0 && <div className="p-4 text-sm">No results.</div>}
          {data?.items.map((it) => (
            <ResultRow key={it.slug} it={it} sel={!!sel[it.slug]} tags={tags[it.slug]}
              onToggle={() => setSel((s) => ({ ...s, [it.slug]: !s[it.slug] }))}
              onTags={(v) => setTags((t) => ({ ...t, [it.slug]: v }))} />
          ))}
        </div>
        {data && (
          <div className="p-4 border-t-2 border-black flex items-center gap-2">
            <button onClick={() => setSel(Object.fromEntries(data.items.map((i) => [i.slug, true])))} data-testid="seasonal-select-all" className="brut bg-white px-2 py-1 font-mono uppercase text-[10px] brut-press">All</button>
            <button onClick={() => setSel({})} data-testid="seasonal-select-none" className="brut bg-white px-2 py-1 font-mono uppercase text-[10px] brut-press">None</button>
            <button onClick={commit} disabled={busy || !chosen.length} data-testid="seasonal-import-commit" className="ml-auto brut pcs-bg-pink text-white px-4 py-2 font-bold uppercase text-xs brut-press disabled:opacity-50 inline-flex items-center gap-1">
              <Check className="w-3.5 h-3.5" strokeWidth={3} /> Import {chosen.length} selected
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
